import { createTheme } from "@mui/material";

interface Shades {
  p4?: string,
  p8?: string,
  p12?: string,
  p30?: string,
  p50?: string,
  p70?: string,
  p90?: string,
  p100?: string,
  p160?: string, // alpha(0.4) or HEXA #xxxxxx66
  p190?: string, // alpha(0.1) or HEXA #xxxxxx1A
}

declare module '@mui/material/styles/createPalette' {
  // main colour with varied opacities
  interface SimplePaletteColorOptions {
    shade?: Shades,
  }

  interface PaletteColor {
    main: string,
    shade?: Shades,
    25?: string,
    50?: string,
    100?: string,
    200?: string,
    300?: string,
    400?: string,
    500?: string,
    600?: string,
    700?: string,
    800?: string,
    900?: string,
  }

  interface PaletteOptions {
    errorSecondary?: PaletteColor,
    customGrey?: PaletteColor,
    tertiary: PaletteColor,
  }

  interface Palette {
    errorSecondary?: PaletteColor,
    customGrey?: PaletteColor,
    tertiary: PaletteColor,
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    customSubtitle: React.CSSProperties
    customBody: React.CSSProperties
    customBody2?: React.CSSProperties
    buttonMedium: React.CSSProperties
    buttonSmall: React.CSSProperties
    textMedium: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    customSubtitle?: React.CSSProperties
    customBody?: React.CSSProperties
    customBody2?: React.CSSProperties
    buttonMedium?: React.CSSProperties
    buttonSmall?: React.CSSProperties
    textMedium?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    buttonMedium: true
    buttonSmall: true
  }
}

const Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: "#1B1B4B",
      light: "#53e3fd",
      dark: "#1b1b4b",
      shade: {
        p4: "#ebf6fe",
        p8: "#E6F2FF",
        p12: "#e2e8f0"
      }
    },
    secondary: {
      main: "#e31e25",
      shade: {
        p4: "#fae8eb",
      }
    },
    tertiary: {
      main: "#0091D5",
      light: "#187994",
      dark: "#1B1B4B",
      contrastText: "#ffffff",
    },
    text: {
      primary: "#1b1b4b",
      disabled: "#cccccc",
    },
    background: {
      default: "#ffffff",
    },
    grey: {
      50: "#f9fafc",
      100: "#E5E1E6"
    }
  },
    typography: {
    fontFamily: [
      '"Inter"',
      'sans-serif',
    ].join(','),
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
    },
    body2: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '20px',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    caption: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '28px',
      letterSpacing: '0.01em',
    },
    overline: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '32px',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    h1: {
      fontWeight: 700,
      fontSize: '72px',
      lineHeight: '90px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    h2: {
      fontWeight: 700,
      fontSize: '60px',
      lineHeight: '72px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    h3: {
      fontWeight: 700,
      fontSize: '48px',
      lineHeight: '60px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    h4: {
      fontWeight: 700,
      fontSize: '36px',
      lineHeight: '44px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    h5: {
      fontWeight: 700,
      fontSize: '30px',
      lineHeight: '38px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    h6: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.02em',
      fontFamily: '\'Space Grotesk\',sans-serif',
    },
    buttonSmall: {
      fontFamily: '\'Space Grotesk\',sans-serif',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '14px',
    },
    buttonMedium: {
      fontFamily: '\'Space Grotesk\',sans-serif',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '20px',
    },
  }
});

export default Theme;
